import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createPreviewActions,
  ShareablePreview,
} from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import { Switch } from "@/core/components/ui/switch";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCreateChartOfAccount } from "@/features/ledger/api/use-create-chart-of-account";
import { useUpdateChartOfAccount } from "@/features/ledger/api/use-update-chart-of-account";
import type { ChartOfAccountDto } from "@/features/ledger/api/dto";
import { useLedgerTranslation } from "@/features/ledger/i18n/use-ledger-translation";
import type { ChartOfAccountRow } from "@/features/ledger/components/chart-of-accounts/chart-of-accounts-columns";
import {
  createChartOfAccountSchema,
  type ChartOfAccountFormValues,
} from "@/features/ledger/schemas/chart-of-account.schema";
import { ACCOUNT_TYPES } from "@/features/shared/api/enums";
import { useAccountSelectOptions } from "@/features/shared/api";

const EMPTY_VALUES: ChartOfAccountFormValues = {
  code: "",
  label: "",
  accountType: "LIABILITY",
  parentAccountId: "",
  businessLine: "",
  isCommissionAccount: false,
  currency: "GNF",
  isActive: true,
};

const toPayload = (values: ChartOfAccountFormValues): ChartOfAccountDto => ({
  code: values.code.trim(),
  label: values.label.trim(),
  accountType: values.accountType,
  parentAccountId: values.parentAccountId?.trim() || null,
  businessLine: values.businessLine?.trim() || null,
  isCommissionAccount: values.isCommissionAccount,
  currency: values.currency,
  isActive: values.isActive,
});

/**
 * Formulaire de création / mise à jour d'un compte comptable
 * (`POST /ChartOfAccounts/add`, `PUT /ChartOfAccounts/{id}`, guide §6.4) —
 * réservé à flashpay-superadmin / flashpay-admin. Preview ShareablePreview
 * avant submit (règle projet ; pas de flux d'argent → pas de section frais).
 */
export function ChartOfAccountFormDialog({
  account,
  open,
  onOpenChange,
}: {
  /** Compte à éditer — absent = création. */
  account?: ChartOfAccountRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useLedgerTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : reconstruit le schéma zod traduit à chaque changement de langue.
  const schema = useMemo(() => createChartOfAccountSchema(), [i18n.language]);

  const create = useCreateChartOfAccount();
  const update = useUpdateChartOfAccount();
  const pending = create.isPending || update.isPending;
  const [preview, setPreview] = useState<ChartOfAccountDto | null>(null);
  const { options: accountOptions, isLoading: accountsLoading } =
    useAccountSelectOptions(open);
  const parentAccountOptions = useMemo(
    () =>
      account?.id
        ? accountOptions.filter((option) => option.value !== account.id)
        : accountOptions,
    [account?.id, accountOptions]
  );

  const initialValues: ChartOfAccountFormValues = account
    ? {
        code: account.code,
        label: account.label,
        accountType: account.accountType,
        parentAccountId: account.parentAccountId ?? "",
        businessLine: account.businessLine ?? "",
        isCommissionAccount: account.isCommissionAccount ?? false,
        currency: account.currency ?? "GNF",
        isActive: account.isActive ?? true,
      }
    : EMPTY_VALUES;

  const form = useForm({
    defaultValues: initialValues,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(toPayload(parsed.data));
    },
  });

  const handleConfirm = () => {
    if (!preview) return;
    if (account) {
      update.mutate(
        { chartOfAccountId: account.id, body: preview },
        {
          onSuccess: () => {
            notifSilence(t("ledger.accounts.feedback.updated"));
            setPreview(null);
            onOpenChange(false);
          },
          onError: (error) => notifSilenceError(error.message),
        }
      );
    } else {
      create.mutate(preview, {
        onSuccess: () => {
          notifSilence(t("ledger.accounts.feedback.created"));
          setPreview(null);
          onOpenChange(false);
        },
        onError: (error) => notifSilenceError(error.message),
      });
    }
  };

  const fieldError =
    (name: keyof ChartOfAccountFormValues) => (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {account
                ? t("ledger.accounts.edit")
                : t("ledger.accounts.create")}
            </DialogTitle>
          </DialogHeader>
          <form
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field
              name="code"
              validators={{ onChange: fieldError("code") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="code">{t("ledger.accounts.form.code")}</Label>
                  <Input
                    id="code"
                    className="notranslate"
                    placeholder={t("ledger.accounts.form.codePlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="label"
              validators={{ onChange: fieldError("label") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="label">
                    {t("ledger.accounts.form.label")}
                  </Label>
                  <Input
                    id="label"
                    className="notranslate"
                    placeholder={t("ledger.accounts.form.labelPlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="accountType">
              {(field) => (
                <div className="space-y-1.5">
                  <Label>{t("ledger.accounts.form.accountType")}</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) =>
                      field.handleChange(
                        v as ChartOfAccountFormValues["accountType"]
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACCOUNT_TYPES.map((v) => (
                        <SelectItem key={v} value={v} className="notranslate">
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Field
              name="parentAccountId"
              validators={{ onChange: fieldError("parentAccountId") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="parentAccountId">
                    {t("ledger.accounts.form.parentAccountId")}
                  </Label>
                  <EntitySearchableSelect
                    id="parentAccountId"
                    options={parentAccountOptions}
                    value={field.state.value ?? ""}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={t(
                      "ledger.accounts.form.parentAccountIdPlaceholder"
                    )}
                    isLoading={accountsLoading}
                    clearable
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="businessLine">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="businessLine">
                    {t("ledger.accounts.form.businessLine")}
                  </Label>
                  <Input
                    id="businessLine"
                    className="notranslate"
                    placeholder={t(
                      "ledger.accounts.form.businessLinePlaceholder"
                    )}
                    value={field.state.value ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field
              name="currency"
              validators={{ onChange: fieldError("currency") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="currency">
                    {t("ledger.accounts.form.currency")}
                  </Label>
                  <Input
                    id="currency"
                    className="uppercase notranslate"
                    maxLength={3}
                    placeholder={t("ledger.accounts.form.currencyPlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) =>
                      field.handleChange(e.target.value.toUpperCase())
                    }
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="isCommissionAccount">
              {(field) => (
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    id="isCommissionAccount"
                    checked={field.state.value}
                    onCheckedChange={(v) => field.handleChange(v)}
                  />
                  <Label htmlFor="isCommissionAccount">
                    {t("ledger.accounts.form.isCommissionAccount")}
                  </Label>
                </div>
              )}
            </form.Field>

            <form.Field name="isActive">
              {(field) => (
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    id="isActive"
                    checked={field.state.value}
                    onCheckedChange={(v) => field.handleChange(v)}
                  />
                  <Label htmlFor="isActive">
                    {t("ledger.accounts.form.isActive")}
                  </Label>
                </div>
              )}
            </form.Field>

            <DialogFooter className="sm:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? <ButtonSpinner /> : null}
                {t("common.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {preview ? (
        <ShareablePreview
          data={preview}
          title={
            account
              ? t("ledger.accounts.preview.updateTitle")
              : t("ledger.accounts.preview.createTitle")
          }
          description={t("ledger.accounts.preview.description")}
          mode="user"
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen && !pending) setPreview(null);
          }}
          maxHeight="90vh"
          actions={createPreviewActions({
            onConfirm: handleConfirm,
            onCancel: () => setPreview(null),
            t,
            confirmLoading: pending,
            withIndicatorOnConfirm: true,
          })}
        />
      ) : null}
    </>
  );
}
